/* Repository path: blue_brief_agentic_workflow/frontend/src/features/estimates/PlanItemDetail.tsx */

import { useEffect, useRef, type ReactNode } from "react";
import type { PlanItem, PlanningBlock, SourceReference } from "../../types";
import { Button } from "../../components/Button";
import { ExplainerDisclosureList } from "../../components/ExplainerDisclosure";
import { StatusChip } from "../../components/StatusChip";
import { TimingLine } from "../../components/TimingLine";
import { formatCurrency, formatDate } from "../../utils/format";
import { budgetContextFor, unbudgetedRiskReasonCopy, type BudgetContext } from "./itemBudgetContext";

const CHIP: Record<
  PlanItem["calculation_state"],
  { tone: "info" | "neutral" | "warning"; label: string }
> = {
  calculated: { tone: "info", label: "Estimate" },
  needs_more_information: { tone: "neutral", label: "Needs more information" },
  identified_only: { tone: "warning", label: "Applies — check officially" },
};

const CONFIDENCE_COPY: Record<PlanItem["confidence"], string> = {
  high: "high — based on reviewed official rates for your situation",
  medium: "medium — based on reviewed official rates; confirm against your assessment",
  low: "low — calculated from reviewed inputs and rules; see the assumptions and source",
};

const KIND_SENTENCE: Record<PlanItem["kind"], string> = {
  obligation: "This is a payment you may owe.",
  benefit: "This is money you may be able to claim.",
  action: "This is something to do.",
  warning: "This is something to check.",
};

const AMOUNT_HEADING: Record<PlanItem["kind"], string> = {
  obligation: "What it may cost",
  benefit: "What you may receive",
  action: "What to check",
  warning: "What to check",
};

function formatRange(min: string, max: string, currency: PlanItem["currency"] = "EUR"): string {
  return `${formatCurrency(min, currency)}–${formatCurrency(max, currency)}`;
}

/**
 * Shared by the timing sources (block 5) and the official sources (block
 * 9): both are the same `SourceReference` shape, rendered the same way the
 * card's former inline disclosure always rendered it. Renders nothing for
 * an empty list, never an empty heading.
 */
function SourceLinks({ sources }: { sources: SourceReference[] }) {
  if (sources.length === 0) return null;
  return (
    <>
      {sources.map((source) => (
        <p key={`${source.url}-${source.reviewed_at}`}>
          <a href={source.url} rel="noreferrer">
            {source.publisher}: {source.title}
          </a>
          <br />
          <small>Reviewed {formatDate(source.reviewed_at)}</small>
        </p>
      ))}
    </>
  );
}

/**
 * Block 4 — the amount presentation. Mirrors `PlanItemCard`'s `Amount`
 * exactly for the three calculation states (independent of kind, exactly
 * as the card has always done), then layers on the planning-allowance or
 * unbudgeted-risk content `budgetContextFor` identifies, so a reader gets
 * the actual range or the actual reason rather than only the generic
 * calculation-state sentence.
 */
function WhatItCosts({ item, budgetContext }: { item: PlanItem; budgetContext: BudgetContext }) {
  return (
    <section className="item-detail__section" aria-labelledby="detail-amount-title">
      <h2 id="detail-amount-title">{AMOUNT_HEADING[item.kind]}</h2>

      {item.calculation_state === "calculated" && (
        <>
          <p className="estimate-card__amount">
            {item.min_amount && item.max_amount
              ? formatRange(item.min_amount, item.max_amount, item.currency)
              : formatCurrency(item.amount as string, item.currency)}
          </p>
          <p className="estimate-card__period">
            Estimated for {item.period === "annual" ? "one year" : item.period}
          </p>
          {(item.monthly_equivalent || item.monthly_min_amount) && (
            <p>
              Monthly equivalent:{" "}
              {item.monthly_equivalent
                ? formatCurrency(item.monthly_equivalent, item.currency)
                : formatRange(item.monthly_min_amount as string, item.monthly_max_amount as string, item.currency)}
            </p>
          )}
          {item.breakdown.length > 0 && (
            <>
              <h3>What this is made of</h3>
              <dl className="facts">
                {item.breakdown.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{formatCurrency(row.amount, item.currency)}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </>
      )}

      {item.calculation_state === "needs_more_information" && (
        <p className="estimate-card__uncalculated">
          We need {item.missing_input?.label} to estimate this.
        </p>
      )}

      {item.calculation_state === "identified_only" && (
        <p className="estimate-card__uncalculated">
          This applies to you. We don’t calculate this amount yet.
        </p>
      )}

      {(budgetContext.kind === "planning_allowance" || budgetContext.kind === "one_time_allowance") && (
        <p>
          <StatusChip tone="neutral">Planning allowance</StatusChip>{" "}
          {budgetContext.kind === "planning_allowance"
            ? `A conservative ${formatRange(budgetContext.monthlyMin, budgetContext.monthlyMax)} to set aside each month.`
            : `A conservative ${formatRange(budgetContext.annualMin, budgetContext.annualMax)} one-off cost to plan for separately.`}{" "}
          This is not a bill, an assessment or what you will owe.
        </p>
      )}

      {budgetContext.kind === "unbudgeted_risk" && (
        <p>{unbudgetedRiskReasonCopy(budgetContext.reason)}</p>
      )}
    </section>
  );
}

/** Block 7 — one sentence on reserve/buffer treatment, from the same
 * `budgetContextFor` classification the card renders from, so the two
 * views can never disagree about which state applies. */
function HowThisAffectsYourPlan({ budgetContext }: { budgetContext: BudgetContext }) {
  if (budgetContext.kind === "none") return null;
  const sentence: string | null = {
    in_reserve: "Included in your monthly reserve.",
    calculated_not_in_reserve: "Calculated, but not included in the monthly reserve for this budgeting period.",
    benefit: "Included in potential benefits, never subtracted from payments or reserve.",
    planning_allowance: "A conservative amount to set aside. This is not a bill, an assessment or what you will owe.",
    one_time_allowance: "A conservative one-off amount to plan for separately. This is not a bill, an assessment or what you will owe.",
    unbudgeted_risk: "We don’t put a number on this one yet.",
    not_in_reserve: "Not included in your monthly reserve.",
  }[budgetContext.kind];
  if (!sentence) return null;
  return (
    <section className="item-detail__section" aria-labelledby="detail-plan-effect-title">
      <h2 id="detail-plan-effect-title">How this affects your monthly plan</h2>
      <p>{sentence}</p>
    </section>
  );
}

export interface PlanItemDetailProps {
  item: PlanItem;
  /** The plan's optional `planning` block — the same value `EstimateView`
   * already reads via `planningBlockIfValid`. Absent is a valid state. */
  planning?: PlanningBlock | null;
  /** True only on a saved plan — gates the deadline control exactly as it
   * gates it on `PlanItemCard`. */
  editableTiming?: boolean;
  onSaveDeadline?: (itemCode: string, deadline: string) => Promise<void>;
  onRemoveDeadline?: (itemCode: string) => Promise<void>;
  /** Present only when an input is blocking this item's estimate. */
  onImprove?: (item: PlanItem) => void;
  /**
   * The bare plan path to return to — `/result` or `/plans/<public_id>`,
   * always with no fragment. Rendered as a plain anchor rather than a
   * click handler: navigating to the same pathname with the fragment
   * removed is native browser behaviour (no full reload, since only the
   * fragment differs), and it means the browser's own back button, this
   * link, and a bookmark all go through the exact same code path in
   * `useRouter`. See docs/architecture/adr/ADR-0009.
   */
  backHref: string;
  /**
   * W4-06 forward slot. `undefined` (the default) renders nothing at all —
   * no disabled control, no "coming soon". See
   * docs/architecture/features/W4-05.md §7 and
   * docs/product/DECISION_LOG.md D-W4-05-05.
   */
  recordActual?: ReactNode;
  /**
   * W5-02: the item's `Email reminders` section content — already
   * constructed by the caller with `ItemReminderSection`, the same
   * "caller builds it, this component only places it" division
   * `recordActual` above already uses. `undefined`/`null` renders no
   * section at all, exactly as `recordActual` does. See
   * docs/architecture/features/W5-02.md §4.
   */
  reminder?: ReactNode;
  /**
   * W4-08: the feedback panel for this item, already constructed by the
   * caller with `surface="item_detail"` and this item's code — the same
   * "caller builds it, this component only places it" division
   * `recordActual` above already uses. `undefined` renders nothing; every
   * real caller passes one. See docs/architecture/features/W4-08.md §4.
   */
  feedback?: ReactNode;
}

/**
 * The dedicated per-item detail experience. Reached only from a
 * `PlanItemCard`'s one detail link, addressed by URL fragment
 * (`#detail/<item_code>`) rather than a route the server ever sees — see
 * `../../app/router.ts` and
 * docs/architecture/adr/ADR-0009-client-route-privacy-and-item-detail-addressing.md.
 * Every block below is omitted entirely when it has no data; there is no
 * empty heading and no "unknown" placeholder anywhere in this component.
 */
export function PlanItemDetail({
  item,
  planning = null,
  editableTiming = false,
  onSaveDeadline,
  onRemoveDeadline,
  onImprove,
  backHref,
  recordActual,
  reminder,
  feedback,
}: PlanItemDetailProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const budgetContext = budgetContextFor(item, planning);

  // Moves focus to the item's own heading the moment its detail opens —
  // never to the top of the page — so a keyboard or screen-reader user
  // lands exactly where the new content starts. See
  // docs/design/features/W4-05.md "Responsive/accessibility".
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const chip = CHIP[item.calculation_state];

  return (
    <article className="item-detail">
      <header className="item-detail__header">
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        <h1 ref={headingRef} tabIndex={-1}>
          {item.official_name}{" "}
          <span className="estimate-card__gloss">({item.english_name})</span>
        </h1>
        <p>{KIND_SENTENCE[item.kind]}</p>
      </header>

      <section className="item-detail__section" aria-labelledby="detail-meaning-title">
        <h2 id="detail-meaning-title">What this means</h2>
        <p>{item.english_name}</p>
        <ExplainerDisclosureList explainerKeys={item.explainer_keys} max={Infinity} />
      </section>

      <section className="item-detail__section" aria-labelledby="detail-why-title">
        <h2 id="detail-why-title">Why this may apply to you</h2>
        <p>{item.why_it_may_apply}</p>
        {item.reasons.length > 0 && (
          <ul>
            {item.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </section>

      <WhatItCosts item={item} budgetContext={budgetContext} />

      {item.timing && (
        <section className="item-detail__section" aria-labelledby="detail-timing-title">
          <h2 id="detail-timing-title">When this happens</h2>
          <TimingLine
            itemCode={item.code}
            timing={item.timing}
            editable={editableTiming}
            onSave={onSaveDeadline}
            onRemove={onRemoveDeadline}
          />
          <SourceLinks sources={item.timing.sources} />
        </section>
      )}

      {reminder && (
        <section className="item-detail__section" aria-labelledby="detail-reminder-title">
          <h2 id="detail-reminder-title">Email reminders</h2>
          {reminder}
        </section>
      )}

      {(item.next_action || (item.calculation_state === "needs_more_information" && onImprove)) && (
        <section className="item-detail__section" aria-labelledby="detail-next-title">
          <h2 id="detail-next-title">What to do next</h2>
          {item.next_action && (
            <p className="estimate-card__next-action">{item.next_action}</p>
          )}
          {item.calculation_state === "needs_more_information" && onImprove && (
            <div className="estimate-card__refine">
              <Button onClick={() => onImprove(item)} aria-describedby={`detail-refine-${item.code}`}>
                Improve my estimates
              </Button>
              <p className="field-hint" id={`detail-refine-${item.code}`}>
                We’ll ask you for {item.missing_input?.label} to sharpen this estimate.
              </p>
              <ExplainerDisclosureList explainerKeys={item.missing_input?.explainer_keys} max={2} />
            </div>
          )}
        </section>
      )}

      <HowThisAffectsYourPlan budgetContext={budgetContext} />

      {(item.assumptions.length > 0 || item.rule_version) && (
        <section className="item-detail__section" aria-labelledby="detail-assumptions-title">
          <h2 id="detail-assumptions-title">What we assumed, and how sure we are</h2>
          {item.assumptions.length > 0 && (
            <ul>
              {item.assumptions.map((assumption) => (
                <li key={assumption}>{assumption}</li>
              ))}
            </ul>
          )}
          <p>Confidence: {CONFIDENCE_COPY[item.confidence]}</p>
          {item.rule_version && <p>Rule version: {item.rule_version}</p>}
        </section>
      )}

      {item.sources.length > 0 && (
        <section className="item-detail__section" aria-labelledby="detail-sources-title">
          <h2 id="detail-sources-title">Where this comes from</h2>
          <SourceLinks sources={item.sources} />
        </section>
      )}

      {recordActual && (
        <section className="item-detail__section" aria-labelledby="detail-record-actual-title">
          <h2 id="detail-record-actual-title">Record what actually happened</h2>
          {recordActual}
        </section>
      )}

      {feedback && <section className="item-detail__section">{feedback}</section>}

      <a href={backHref} className="button button--quiet item-detail__back">
        Back to your plan
      </a>
    </article>
  );
}

/**
 * A malformed fragment and a valid code absent from this plan both land
 * here, with identical wording — no existence oracle. See
 * docs/architecture/adr/ADR-0009-client-route-privacy-and-item-detail-addressing.md.
 */
export function ItemDetailNotFound({ backHref }: { backHref: string }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <section className="item-detail item-detail--not-found">
      <h1 ref={headingRef} tabIndex={-1}>
        We can’t find that item in your plan
      </h1>
      <p>
        This link may be from an older plan, or your plan may have changed since. Go back to your
        plan to see what’s in it now.
      </p>
      <a href={backHref} className="button button--quiet">
        Back to your plan
      </a>
    </section>
  );
}


